<script setup>
import { computed } from 'vue';
import { useClipboard } from '@vueuse/core';
import appConfig from '@/app/config/app.js';
import CFButton from '@/ui/Button/CFButton.vue';
import { logout, LogoutReason } from '#modules/auth/services/AuthService.js';

const props = defineProps({
    error: {
        type: [Object, String],
        required: true,
    },
});

const errorDetails = computed(() => {
    const errorMessage = props.error?.message || props.error || 'Unknown error';
    const timestamp = new Date().toISOString();
    const userAgent = navigator.userAgent;

    return `Error Details:
---
Error: ${errorMessage}
Timestamp: ${timestamp}
User Agent: ${userAgent}
---`;
});

const { copy, copied } = useClipboard({ source: errorDetails });

const reloadPage = async () => {
    await logout({
        reason: LogoutReason.AUTO,
    });

    window.location.href = '/';
};
</script>

<template>
    <div class="fixed inset-0 z-50 flex items-center justify-center bg-black">
        <div class="max-w-md px-4 text-center">
            <img
                src="/Cyberfusion_Logo_White.svg"
                alt="Cyberfusion Logo"
                class="mx-auto mb-8 h-12"
            />

            <div class="mb-6 flex justify-center">
                <svg
                    class="h-16 w-16 text-red-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    aria-hidden="true"
                >
                    <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                    />
                </svg>
            </div>

            <h1 class="mb-3 text-2xl font-bold text-white">{{ $t('error.unexpected_title') }}</h1>

            <p class="mb-8 text-gray-300">{{ $t('error.unexpected_description') }}</p>

            <div class="mb-8 flex items-center justify-center gap-3">
                <CFButton
                    color="secondary"
                    @click="reloadPage"
                >
                    {{ $t('button.reload_page') }}
                </CFButton>

                <CFButton
                    color="accent"
                    @click="copy()"
                >
                    {{ copied ? $t('button.copied') : $t('button.copy_error_details') }}
                </CFButton>
            </div>

            <Transition name="fade">
                <div
                    v-if="copied"
                    class="mb-6 text-sm text-green-400"
                >
                    {{ $t('error.details_copied') }}
                </div>
            </Transition>

            <p class="text-sm text-gray-400">
                {{ $t('error.contact_support_prefix') }}
                <a
                    :href="`mailto:${appConfig.supportEmail}`"
                    class="text-indigo-400 underline transition-colors hover:text-indigo-300"
                >
                    {{ appConfig.supportEmail }}
                </a>
            </p>
        </div>
    </div>
</template>

<style scoped>
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
</style>
